import React from 'react';

const RELOAD_KEY = 'coursolo:chunk-reload';

export function isChunkLoadError(error) {
  if (!error) return false;
  const msg = String(error.message || error);
  const name = String(error.name || '');
  return (
    name === 'ChunkLoadError'
    || /Loading chunk [\d]+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Load a dynamic import with retries on ChunkLoadError / failed module fetch.
 * Optionally one sessionStorage-gated full reload after retries are exhausted.
 */
export async function importWithRetry(factory, {
  retries = 2,
  delayMs = 800,
  reloadOnce = true,
  storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  reload = typeof window !== 'undefined' ? () => window.location.reload() : null,
} = {}) {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const mod = await factory();
      try {
        storage?.removeItem?.(RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (error) {
      lastError = error;
      if (!isChunkLoadError(error) || attempt >= retries) break;
      await sleep(delayMs * (attempt + 1));
    }
  }

  if (reloadOnce && isChunkLoadError(lastError) && typeof reload === 'function') {
    try {
      if (!storage?.getItem?.(RELOAD_KEY)) {
        storage?.setItem?.(RELOAD_KEY, '1');
        reload();
        return new Promise(() => {});
      }
    } catch {
      /* ignore */
    }
  }

  throw lastError;
}

/**
 * React.lazy wrapper so post-deploy / flaky-network chunk failures recover
 * instead of landing in the root ErrorBoundary.
 */
export function lazyWithRetry(factory, options) {
  return React.lazy(() => importWithRetry(factory, options));
}

export default lazyWithRetry;
